import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PButtonPure, PIcon, PTable, PTableBody, PTableRow, PTag, PText } from "@porsche-design-system/components-react"
import { spacingStaticXs, spacingStaticSm, spacingFluidSm } from "@porsche-design-system/components-react/emotion"
import type {
	VehicleHistoryListInventoryItem,
	VehicleHistoryListItemChange,
	VehicleHistoryListItemLabelValue,
} from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { Link } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { getDealerAppRoute } from "src/common/routes"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Flex } from "src/components/Flex"
import { TableCellFixed } from "src/components/tableCellFixed/TableCellFixed"
import { VehicleHistoryListTableHead } from "./VehicleHistoryListTableHead"
import { eventTypeMessages } from "./eventType.messages"
import { eventTypeIconMap } from "./eventTypeIconMap.const"
import { groupByEventType } from "./groupByEventType"
import type { GroupedVehicleHistoryListItem } from "./groupByEventType"
import { vehicleHistoryListColumns } from "./vehicleHistoryListColumns.const"

const messages = defineMessages({
	noEntries: {
		defaultMessage: "No entries found.",
		id: "vehicleHistory.list.noEntries",
	},
})

interface VehicleHistoryListProps {
	items: VehicleHistoryListInventoryItem[]
	timeZone?: string
}

export const VehicleHistoryList = ({ items, timeZone }: VehicleHistoryListProps) => {
	const routes = useDealerAppRoutes()
	const { locale } = useDealerContext()
	return (
		<>
			<PTable caption="Vehicle History List" layout="fixed" sticky>
				<VehicleHistoryListTableHead timeZone={timeZone} />
				{items.map((item, index) => {
					const dealershipTagLabel = `${item.dealershipName} | ${item.dealershipId}`
					return (
						<StyledTableBody key={index}>
							{items.length > 1 && (
								<PTableRow>
									<TableCell $width={vehicleHistoryListColumns.dateTime}>
										<DealershipTag variant="info-frosted" icon="pin">
											{routes?.inventoryList?.vehicleDetails ? (
												<Link
													to={`${getDealerAppRoute(item.dealershipId, locale)}${routes.inventoryList.vehicleDetails.url(item.inventoryId)}`}
												>
													{dealershipTagLabel}
												</Link>
											) : (
												dealershipTagLabel
											)}
										</DealershipTag>
									</TableCell>
								</PTableRow>
							)}
							{groupByEventType(item.items).map((eventItem, i) => (
								<VehicleHistoryListRow key={i} item={eventItem} />
							))}
						</StyledTableBody>
					)
				})}
			</PTable>
			{items.length === 0 && (
				<NoResults>
					<PText>
						<FormattedMessage {...messages.noEntries} />
					</PText>
				</NoResults>
			)}
		</>
	)
}

const NoResults = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${spacingStaticSm} 0;
`

interface VehicleHistoryListRowProps {
	isAdditional?: boolean
	item: GroupedVehicleHistoryListItem
}

const VehicleHistoryListRow = ({ isAdditional, item, ...rest }: VehicleHistoryListRowProps) => {
	const [areAdditionalItemsVisible, setAreAdditionalItemsVisible] = useState(false)
	const { formatMessage } = useIntl()

	const toggleAdditionalItems = useCallback(
		() => setAreAdditionalItemsVisible((v) => !v),
		[setAreAdditionalItemsVisible],
	)

	return (
		<>
			<TableRow {...rest} $isAdditional={isAdditional}>
				<TableCell $width={vehicleHistoryListColumns.dateTime}>
					<Flex $gap="xs">
						{item.date}
						{item.time && <PText color="contrast-medium">{item.time}</PText>}
					</Flex>
				</TableCell>
				<TableCell $width={vehicleHistoryListColumns.event}>
					<IconNameTagWrapper>
						<PIcon name={eventTypeIconMap[item.eventType]} />
						<PText weight="semibold" title={formatMessage(eventTypeMessages[item.eventType])} ellipsis>
							<FormattedMessage {...eventTypeMessages[item.eventType]} />
						</PText>
						{item.additionalItems && (
							<PTag variant="info-frosted" compact onClick={toggleAdditionalItems}>
								<button
									type="button"
									aria-label={formatMessage({
										defaultMessage: "Toggle additional items",
										id: "vehicleHistory.list.toggleAdditionalItems",
									})}
								>
									{areAdditionalItemsVisible ? "-" : "+"}
									{item.additionalItems.length}
								</button>
							</PTag>
						)}
					</IconNameTagWrapper>
				</TableCell>
				<TableCell $width={vehicleHistoryListColumns.details}>
					<VehicleHistoryListDetails details={item.details} additionalDetails={item.additionalDetails} />
				</TableCell>
				<TableCell $width={vehicleHistoryListColumns.trigger}>
					<PText title={item.trigger} ellipsis>
						{item.trigger}
					</PText>
				</TableCell>
			</TableRow>
			{areAdditionalItemsVisible &&
				item.additionalItems?.map((additionalItem, index) => (
					<VehicleHistoryListRow isAdditional key={index} item={additionalItem} />
				))}
		</>
	)
}

const VehicleHistoryListDetails = ({
	additionalDetails,
	details,
}: Pick<GroupedVehicleHistoryListItem, "details" | "additionalDetails">) => {
	const [areAdditionalDetailsVisible, setAreAdditionalDetailsVisible] = useState(false)

	const toggleAdditionalDetails = useCallback(
		() => setAreAdditionalDetailsVisible((v) => !v),
		[setAreAdditionalDetailsVisible],
	)

	return !additionalDetails || additionalDetails.length === 0 ? (
		<Details details={details} />
	) : (
		<>
			<PButtonPure
				alignLabel="start"
				icon={areAdditionalDetailsVisible ? "arrow-head-up" : "arrow-head-down"}
				onClick={toggleAdditionalDetails}
			>
				<Details details={details} />
			</PButtonPure>
			{areAdditionalDetailsVisible &&
				additionalDetails.map((detail, index) => <Details key={index} details={detail} />)}
		</>
	)
}

const Details = ({ details }: { details: string | VehicleHistoryListItemLabelValue }) => (
	<PText ellipsis>
		{typeof details === "string" ? (
			details
		) : (
			<>
				{details.label && <strong>{details.label}:&nbsp;</strong>}
				{typeof details.value === "string" ? details.value : <Change {...details.value} />}
			</>
		)}
	</PText>
)

const Change = ({ current, previous }: VehicleHistoryListItemChange) => (
	<>
		<s>{previous}</s>&nbsp;
		<Arrow />
		&nbsp;
		{current}
	</>
)

// as we don't have row links, but only buttons for details, we don't need the default hover effect just like for skeleton table
const StyledTableBody = styled(PTableBody)`
	--_p-table-b: none;
`

const DealershipTag = styled(PTag)`
	margin-inline-start: calc(${spacingFluidSm} * -1);
`

const TableRow = styled(PTableRow, { shouldForwardProp })<{ $isAdditional?: boolean }>`
	${({ $isAdditional }) =>
		$isAdditional &&
		css`
			background: #f6f9ff;
			--_p-table-b: #f6f9ff;
		`};
`

const TableCell = styled(TableCellFixed)`
	vertical-align: top;
`

const IconNameTagWrapper = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, auto) auto;
	justify-content: start;
	gap: ${spacingStaticXs};
`

const Arrow = styled.span`
	&::before {
		content: "→";
	}

	&:dir(rtl)::before {
		content: "←";
	}
`
